/**
 * The account on the server (packages/server/src/account-routes.ts). Unlike bookings these calls
 * need the network; the app says so instead of waiting.
 */
import { DEMO_PRIVATE_CELLAR, DEMO_WINE_LIST, demoPantry, planImport, plannedReceipt, uuidv7 } from "@lagerlog/core";
import { IS_PANTRY, MAIN_PROFILE, STARTERS, ownsLager } from "./brand.ts";
import type { Access, Booking, Grant, Place, Role } from "@lagerlog/core";
import { api, store } from "./store.ts";

export interface User {
  id: string;
  username: string;
  displayName: string;
  email: string | null;
  /** Counts for mails („Passwort vergessen?“) once confirmed. */
  emailConfirmed?: boolean;
  /** A new address waiting for its link while the confirmed one still counts. */
  emailPending?: string | null;
}

export interface Membership {
  id: string;
  name: string;
  role: Role;
  kind: "business" | "private";
}

export interface SignedIn {
  user: User;
  workspaces: Membership[];
}

export interface Session {
  id: string;
  device: string;
  createdAt: number;
  lastSeenAt: number;
  current: boolean;
}

export interface Passkey {
  id: string;
  name: string;
  createdAt: number;
  lastUsedAt: number | null;
  backedUp: boolean;
}

const json = (method: string, body?: unknown): RequestInit => ({ method, ...(body === undefined ? {} : { body: JSON.stringify(body) }) });

/** The word for a workspace in this app (docs/13-profil-wein.md, Vokabular). */
export const wordFor = (kind: string | undefined) => MAIN_PROFILE.vocabulary.workspace[kind === "private" ? "private" : "business"];

/** The device's name in the server's list of sessions: enough to tell a phone from a laptop. */
export function deviceName(): string {
  const agent = navigator.userAgent;
  return /iPhone/.test(agent) ? "iPhone" : /iPad|Macintosh.*Mobile/.test(agent) ? "iPad" : /Android/.test(agent) ? "Android" : /Macintosh/.test(agent) ? "Mac" : /Windows/.test(agent) ? "Windows" : "Browser";
}

export interface TeamMember {
  id: string;
  displayName: string;
  role: Role;
  me: boolean;
  /** Granted storages (owners only see them); null for owners, who have all. */
  lagers?: Record<string, Grant> | null;
  username?: string;
  hasEmail?: boolean;
  reset?: { expiresAt: number } | null;
  passkeys?: number;
}

export interface Invite {
  id: string;
  name: string;
  expiresAt: number;
  expired: boolean;
  role: Role;
  lagers: Record<string, Grant>;
}

/** Role and storages of a member or an invitation (F20). */
export interface MemberAccess {
  role: Role;
  lagers: Record<string, Grant>;
}

export type LinkInfo =
  | { purpose: "join"; name: string; workspace: string; kind: "business" | "private"; expiresAt: number }
  | { purpose: "reset"; name: string; username: string; expiresAt: number; passkeys: number; self?: boolean };

/**
 * What this server offers; fetched once, the answer does not change while the app runs. Since the
 * move to mzapps.cloud (27.10.2026) also where the app lives: `movedTo` at an old address, the new
 * one; `movedFrom` at the new address, the old one (for „Anmeldung mitnehmen“).
 */
export interface ServerFeatures {
  open: boolean;
  passkeys: boolean;
  mail: boolean;
  forgot: boolean;
  movedTo: string | null;
  movedFrom: string | null;
}
let features: Promise<ServerFeatures> | null = null;
export const serverFeatures = () => (features ??= api<{ open: boolean; passkeys?: boolean; mail?: boolean; forgot?: boolean; movedTo?: string; movedFrom?: string }>(null, "/api/signup").then((r) => ({ open: r.open, passkeys: r.passkeys === true, mail: r.mail === true, forgot: r.forgot === true, movedTo: r.movedTo ?? null, movedFrom: r.movedFrom ?? null })).catch((err) => {
  features = null;
  throw err;
}));

/**
 * Behind every place that announces a mail (Martin, 31.10.2026; section 13 of ANMELDUNG-EINHEITLICH.md
 * in SpeisePlan, word for word in all apps): mails from noreply@mzapps.cloud sometimes land in spam.
 * The apps here are German only; SpeisePlan's English: „If no email arrives, check your spam folder.“
 */
export const SPAM_FOLDER = "Kommt keine Mail, sieh im Spam-Ordner nach.";
/** „Noch nicht bestätigt“ in „Mein Konto“ (English: „Not confirmed yet. Check your inbox, including the spam folder.“). */
export const NOT_CONFIRMED = "Noch nicht bestätigt. Sieh in deinem Postfach nach, auch im Spam-Ordner.";
/** The confirmation link is out. */
export const mailSent = (email: string) => `Wir haben dir eine Mail an ${email} geschickt. Bestätige die Adresse mit dem Link darin. ${SPAM_FOLDER}`;

/** Requests for the workspace this device holds. */
const here = () => ({ workspace: store.config?.workspaceId });

export const team = {
  members: () => api<{ role: Role; members: TeamMember[]; invites: Invite[]; lagers?: { id: string; name: string }[] }>(here(), "/api/workspace/members"),
  invite: (name: string, access: MemberAccess) => api<{ invite: Invite; url: string }>(here(), "/api/workspace/invites", json("POST", { name, ...access })),
  setInviteAccess: (id: string, access: MemberAccess) => api(here(), `/api/workspace/invites/${encodeURIComponent(id)}`, json("PATCH", access)),
  setAccess: (id: string, access: Partial<MemberAccess>) => api(here(), `/api/workspace/members/${encodeURIComponent(id)}`, json("PATCH", access)),
  renew: (id: string) => api<{ invite: Invite; url: string }>(here(), `/api/workspace/invites/${encodeURIComponent(id)}/link`, json("POST")),
  withdraw: (id: string) => api(here(), `/api/workspace/invites/${encodeURIComponent(id)}`, json("DELETE")),
  remove: (id: string) => api(here(), `/api/workspace/members/${encodeURIComponent(id)}`, json("DELETE")),
  resetLink: (id: string) => api<{ url: string; expiresAt: number }>(here(), `/api/workspace/members/${encodeURIComponent(id)}/link`, json("POST")),
  rename: (name: string) => api<{ name: string }>(here(), "/api/workspace/name", json("PUT", { name })),
  check: (token: string) => api<LinkInfo>(null, "/api/links/check", json("POST", { token })),
  join: (token: string, body: { displayName: string; email: string; username: string; password: string }) =>
    api<SignedIn>(null, "/api/links/join", json("POST", { token, ...body, device: deviceName() })),
  joinWithAccount: (token: string) => api<{ workspace: Membership; workspaces: Membership[] }>(null, "/api/me/join", json("POST", { token })),
  reset: (token: string, password: string, removePasskeys: boolean) => api<SignedIn>(null, "/api/links/reset", json("POST", { token, password, removePasskeys, device: deviceName() })),
};

/** "Montag, 21. Oktober" */
export const untilDay = (at: number) => new Date(at).toLocaleDateString("de-DE", { weekday: "long", day: "numeric", month: "long" });

/**
 * How long a link holds: "Montag, 21. Oktober", or for one by mail that ends within a day
 * "heute, 14:35 Uhr" and, shortly before midnight, "morgen, 00:20 Uhr" (common rules, section 19).
 */
export function untilWhen(at: number, now = Date.now()): string {
  if (at - now >= 86_400_000) return untilDay(at);
  const day = (t: number) => new Date(t).toDateString();
  const time = new Date(at).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
  return `${day(at) === day(now) ? "heute" : "morgen"}, ${time} Uhr`;
}

export const account = {
  signupOpen: () => serverFeatures().then((f) => f.open),
  signup: (body: { workspace: string; kind: string; displayName: string; email: string; username: string; password: string }) =>
    api<SignedIn>(null, "/api/signup", json("POST", { ...body, device: deviceName() })),
  login: (username: string, password: string) => api<SignedIn>(null, "/api/login", json("POST", { username, password, device: deviceName() })),
  /** „Anmeldung mitnehmen“ (handover.ts): the new address with a key for 60 seconds. */
  handover: () => api<{ url: string }>(null, "/api/me/handover", json("POST")),
  /** At the new address: the key from the old one signs in. */
  redeemHandover: (key: string) => api<SignedIn>(null, "/api/handover", json("POST", { key, device: deviceName() })),
  /** „Passwort einmal eingeben“: only checked; the password manager saves it for this address. */
  checkPassword: (password: string) => api<{ ok: true }>(null, "/api/me/password/check", json("POST", { password })),
  /** „Passwort vergessen?“: a link by mail; the same answer whether the account exists or not. */
  forgot: (who: string) => api<{ ok: true }>(null, "/api/password/forgot", json("POST", { who })),
  me: () => api<SignedIn & { session: string; demo?: boolean }>(null, "/api/me"),
  setName: (displayName: string) => api<{ user: User }>(null, "/api/me/name", json("PUT", { displayName })),
  setEmail: (email: string) => api<{ user: User; mailed: "sent" | "limited" | "none" }>(null, "/api/me/email", json("PUT", { email })),
  /** „Mail noch einmal schicken“: the link for the address waiting to be confirmed. */
  emailAgain: () => api<{ email: string }>(null, "/api/me/email/again", json("POST")),
  /** #/email/<token>: confirms, only signed in as the account the link is for. */
  confirmEmail: (token: string) => api<{ user: User }>(null, "/api/me/email/confirm", json("POST", { token })),
  setPassword: (current: string, next: string) => api(null, "/api/me/password", json("PUT", { current, next })),
  sessions: () => api<{ sessions: Session[] }>(null, "/api/me/sessions").then((r) => r.sessions),
  endSession: (id: string) => api(null, `/api/me/sessions/${encodeURIComponent(id)}`, json("DELETE")),
  passkeys: () => api<{ passkeys: Passkey[] }>(null, "/api/me/passkeys").then((r) => r.passkeys),
  removePasskey: (id: string) => api(null, `/api/me/passkeys/${encodeURIComponent(id)}`, json("DELETE")),

  /** Creates a passkey on this device for the signed-in account. */
  async addPasskey(name: string): Promise<Passkey[]> {
    const { challenge, options } = await api<{ challenge: string; options: PublicKeyCredentialCreationOptionsJSON }>(null, "/api/me/passkeys/options", json("POST"));
    const credential = (await navigator.credentials.create({ publicKey: PublicKeyCredential.parseCreationOptionsFromJSON(options) })) as PublicKeyCredential | null;
    if (!credential) throw new Error("Abgebrochen");
    return api<{ passkeys: Passkey[] }>(null, "/api/me/passkeys", json("POST", { challenge, credential: credential.toJSON(), name })).then((r) => r.passkeys);
  },

  /** Signs in with a passkey of this device, without user name. */
  async loginWithPasskey(): Promise<SignedIn> {
    const { challenge, options } = await api<{ challenge: string; options: PublicKeyCredentialRequestOptionsJSON }>(null, "/api/passkeys/login/options", json("POST"));
    const credential = (await navigator.credentials.get({ publicKey: PublicKeyCredential.parseRequestOptionsFromJSON(options) })) as PublicKeyCredential | null;
    if (!credential) throw new Error("Abgebrochen");
    return api<SignedIn>(null, "/api/passkeys/login", json("POST", { challenge, credential: credential.toJSON(), device: deviceName() }));
  },
};

/** Passkeys need a browser that reads the server's options as JSON (Safari 17.4, Chrome 129). */
export const passkeysSupported = () =>
  typeof PublicKeyCredential !== "undefined" && typeof PublicKeyCredential.parseRequestOptionsFromJSON === "function" && window.isSecureContext;

/** A cancelled passkey dialog is no error worth showing. */
export const cancelled = (err: unknown) => err instanceof DOMException && (err.name === "NotAllowedError" || err.name === "AbortError");

/** The storages of a workspace the device may hold: only the granted ones come (F20), and only those of this app (KellerLog or VorratsBuch); and what the member may. */
export async function lagersOf(workspace: string): Promise<{ lagers: { id: string; name: string }[]; access: Access | null }> {
  const load = await api<{ entities: { type: string; id: string; fields: Record<string, unknown> }[]; access?: Access }>({ workspace }, "/api/load?lager=");
  const lagers = load.entities.filter((e) => e.type === "lager" && e.fields.archived !== true && ownsLager(e.fields.profile)).map((e) => ({ id: e.id, name: typeof e.fields.name === "string" ? e.fields.name : e.id }));
  return { lagers, access: load.access ?? null };
}

/**
 * An empty workspace gets the invented Weinbar Rebstock: the storages of the wine profile and the 22
 * wines of its wine list with opening stock, through the same import as a real wine list. A private
 * cellar gets the cellar of Familie Berger instead.
 */
export async function fillWithDemo(kind?: string) {
  await fillWithStarter(STARTER.map((l) => l.id));
  if (IS_PANTRY) return fillWithPantryDemo();
  const places: Place[] = STARTER.flatMap((l) => l.locations.map(([id, name]) => ({ id: `${l.id}/${id}`, lager: l.id, label: `${l.name} · ${name}`, lagerName: l.name, name })));
  const plan = planImport(kind === "private" ? DEMO_PRIVATE_CELLAR : DEMO_WINE_LIST, [], places, () => uuidv7(Date.now()));
  for (const r of plan.rows) {
    await store.edit("article", r.id, r.fields);
    for (const s of r.stock) {
      await store.book(plannedReceipt(r, s, "demo") as Booking);
    }
  }
}

/** The storages this app suggests: the wine profile's in KellerLog (docs/13-profil-wein.md), Vorratsschrank, Kühlschrank, Gefriertruhe in VorratsBuch. */
export const STARTER = STARTERS;

/** The pantry of the invented Familie Sommer (packages/core/src/demo-pantry.ts) with stock and best-before dates. */
async function fillWithPantryDemo() {
  for (const row of demoPantry(Date.now(), () => uuidv7(Date.now()))) {
    await store.edit("article", row.id, row.fields);
    for (const b of row.receipts) await store.book(b as Booking);
  }
}

export async function fillWithStarter(lagers: string[]) {
  for (const lager of STARTER.filter((l) => lagers.includes(l.id))) {
    await store.edit("lager", lager.id, { name: lager.name, profile: lager.profile });
    for (const [id, name] of lager.locations) await store.edit("location", `${lager.id}/${id}`, { lager: lager.id, name });
  }
}
